// = Libs =
import useSWR from "swr";
import sendRequest from "@/helpers/sendRequest";

// = Helpers =

interface SectionModel {
  sectionId: number;
  title: string;
}

const useSections = (pollId: number | string): SectionModel[] => {
  const { data, error } = useSWR(`monitoring-api/polls/${pollId}/sections`);

  if (!data) return error;

  return data?.items;
};

const getSections = async (pollId: number | string) => {
  const { error, data } = await sendRequest<{ items: SectionModel[] }>({
    url: `monitoring-api/polls/${pollId}/sections/`,
    method: 'get',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, data };
};

const updateSection = async (
  pollId: number | string,
  sectionData: SectionModel
) => {
  const { error, data: SectionModel } = await sendRequest<{
    sectionId: number;
  }>({
    url: `monitoring-api/polls/${pollId}/sections/${sectionData.sectionId}/`,
    method: 'put',
    body: {
      title: sectionData.title,
    },
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, id: SectionModel };
};

const deleteSection = async (pollId: number | string, sectionId: number) => {
  const { error } = await sendRequest<{ pollId: string }>({
    url: `monitoring-api/polls/${pollId}/sections/${sectionId}/`,
    method: 'delete',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error };
};

const createSection = async (pollId: number | string, title?: string) => {
  const { error, data: SectionModel } = await sendRequest<{ pollId: string }>({
    url: `monitoring-api/polls/${pollId}/sections/`,
    method: 'post',
    body: {
      title: title || 'Новый раздел',
    },
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, id: SectionModel?.sectionId };
};

interface AnswerModel {
  answerId?: number;
  questionId?: number;
  reportId?: number;
  value: any;
}

interface QuestionModel {
  questionId: number;
  sectionId: number;
  type: 'radio' | 'checkbox' | 'string' | 'int' | 'file';
  title: string;
  addition: {
    choice: {};
  };
  isColumn?: boolean;
  answer?: AnswerModel;
}

const useQuestions = (pollId: number | string): QuestionModel[] => {
  const { data, error } = useSWR(`monitoring-api/polls/${pollId}/questions`);

  if (!data) return error;

  return data?.items;
};

const getQuestions = async (pollId: number | string) => {
  const { error, data } = await sendRequest<{ items: QuestionModel[] }>({
    url: `monitoring-api/polls/${pollId}/questions/`,
    method: 'get',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, data };
};

const updateQuestion = async (
  pollId: number | string,
  questionData: QuestionModel
) => {
  const { error, data: QuestionModel } = await sendRequest({
    url: `monitoring-api/polls/${pollId}/questions/${questionData.questionId}/`,
    method: 'put',
    body: questionData,
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, id: QuestionModel?.questionId };
};

const createQuestion = async (
  pollId: number | string,
  questionData: {
    sectionId: number;
    title?: string;
    type?: string;
    addition?: any;
  }
) => {
  const { error, data: QuestionModel } = await sendRequest({
    url: `monitoring-api/polls/${pollId}/questions/`,
    method: 'post',
    body: {
      title: 'Новый вопрос',
      type: 'string',
      addition: {},
      ...questionData,
      isColumn: false,
    },
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, id: QuestionModel?.questionId };
};

const deleteQuestion = async (pollId: number | string, questionId: number) => {
  const { error } = await sendRequest({
    url: `monitoring-api/polls/${pollId}/questions/${questionId}/`,
    method: 'delete',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error };
};

const importQuestions = async (fromId: number, toId: number) => {
  const { error, data: QuestionModel } = await sendRequest({
    url: `monitoring-api/polls/${toId}/import-from/${fromId}/`,
    method: 'put',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, id: QuestionModel?.questionId };
};

const getAggregated = async (pollId: number, questionId: number) => {
  const { error, data } = await sendRequest<{ result: string }>({
    url: `monitoring-api/polls/${pollId}/questions/${questionId}/aggregate/?type=sum`,
    method: 'get',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, data };
};

export {
  useSections,
  getSections,
  updateSection,
  createSection,
  deleteSection,
  useQuestions,
  updateQuestion,
  createQuestion,
  deleteQuestion,
  getQuestions,
  getAggregated,
  importQuestions,
};
export type { SectionModel, QuestionModel };
